import type { Request } from 'express';
import type { Logger } from '@finn-no/tjt-common-node';

/**
 * Extract all values from a potentially comma-separated query param into a string array.
 * Empty values are filtered out. When more than one value is present, an info log is emitted.
 *
 * Example input: `"123,456"` with paramName `"itemIds"`
 * output: `["123", "456"]` (and logs `Multiple itemIds provided: 123,456`)
 *
 * @param param a query param value (e.g. `req.query.itemIds`).
 * @param paramName the name of the param, used for logging.
 * @param logger a logger used to emit an info message when multiple values are provided.
 * @returns an array of the non-empty comma-separated values.
 */
const extractAllValues = (
    param: Request['query'][string],
    paramName: string,
    logger: Logger,
): string[] => {
    const str = param?.toString() ?? '';
    if (!str) return [];

    const values = str.split(',').filter((value) => value !== '');
    if (values.length > 1) {
        logger.info(`Multiple ${paramName} provided: ${values.join(',')}`);
    }
    return values;
};

export default extractAllValues;
